import { Button, Group, Modal, Paper, SegmentedControl, Stack, Switch, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkDelete, useBulkUpdate } from '@/api/timeEntries';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { ProjectPicker, type ProjectSelection } from '@/components/ProjectPicker';
import { TagPicker } from '@/components/TagPicker';
import { useApiError } from '@/hooks/useApiError';

/** Massenbearbeitung ausgewählter Einträge (TT-07). */
export function BulkBar({ ids, onDone }: { ids: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const { workspaceId } = useCurrentWorkspace();
  const onError = useApiError();
  const bulkUpdate = useBulkUpdate(workspaceId);
  const bulkDelete = useBulkDelete(workspaceId);
  const [open, setOpen] = useState(false);
  const [changeProject, setChangeProject] = useState(false);
  const [selection, setSelection] = useState<ProjectSelection>({});
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagMode, setTagMode] = useState<'ADD' | 'REPLACE' | 'REMOVE'>('ADD');
  const [billable, setBillable] = useState<'keep' | 'yes' | 'no'>('keep');

  const apply = () => {
    bulkUpdate.mutate(
      {
        ids,
        projectId: changeProject ? (selection.projectId ?? undefined) : undefined,
        taskId: changeProject ? (selection.taskId ?? undefined) : undefined,
        clearProject: changeProject && !selection.projectId,
        tagIds: tagIds.length ? tagIds : undefined,
        tagMode: tagIds.length ? tagMode : undefined,
        billable: billable === 'keep' ? undefined : billable === 'yes',
      },
      {
        onSuccess: () => {
          setOpen(false);
          onDone();
        },
        onError,
      },
    );
  };

  return (
    <Paper withBorder p="xs" radius="md" bg="var(--mantine-color-orange-light)">
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          {t('tracker.selectedCount', { count: ids.length })}
        </Text>
        <Group gap="xs">
          <Button size="xs" variant="default" onClick={() => setOpen(true)}>
            {t('tracker.bulkEdit')}
          </Button>
          <Button
            size="xs"
            color="red"
            variant="light"
            loading={bulkDelete.isPending}
            onClick={() => bulkDelete.mutate(ids, { onSuccess: onDone, onError })}
          >
            {t('common.delete')}
          </Button>
          <Button size="xs" variant="subtle" onClick={onDone}>
            {t('common.cancel')}
          </Button>
        </Group>
      </Group>
      <Modal opened={open} onClose={() => setOpen(false)} title={t('tracker.bulkEditTitle', { count: ids.length })}>
        <Stack>
          <Switch label={t('tracker.changeProject')} checked={changeProject} onChange={(e) => setChangeProject(e.currentTarget.checked)} />
          {changeProject && <ProjectPicker variant="field" value={selection} onChange={setSelection} />}
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('tracker.tags')}
            </Text>
            <SegmentedControl
              size="xs"
              value={tagMode}
              onChange={(v) => setTagMode(v as typeof tagMode)}
              data={[
                { value: 'ADD', label: t('tracker.tagsAdd') },
                { value: 'REPLACE', label: t('tracker.tagsReplace') },
                { value: 'REMOVE', label: t('tracker.tagsRemove') },
              ]}
            />
            <TagPicker variant="field" value={tagIds} onChange={setTagIds} />
          </Stack>
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('tracker.billable')}
            </Text>
            <SegmentedControl
              size="xs"
              value={billable}
              onChange={(v) => setBillable(v as typeof billable)}
              data={[
                { value: 'keep', label: t('tracker.keep') },
                { value: 'yes', label: t('common.yes') },
                { value: 'no', label: t('common.no') },
              ]}
            />
          </Stack>
          <Button onClick={apply} loading={bulkUpdate.isPending}>
            {t('common.apply')}
          </Button>
        </Stack>
      </Modal>
    </Paper>
  );
}
